/**
 * Where a project's files live. The dev server stores them on disk; the
 * browser version stores them in IndexedDB. Paths are project paths: forward
 * slashes, relative to the storage's root.
 */
export interface Storage {
  /** A label for the root: an absolute folder on disk, or a database name. */
  readonly root: string;
  read(projectPath: string): Promise<string | undefined>;
  /** Replaces a text file, atomically where the storage can. */
  write(projectPath: string, text: string): Promise<void>;
  /** Writes a new file. Refuses to replace one that exists. */
  create(projectPath: string, bytes: Uint8Array): Promise<void>;
  /** A file's bytes, or undefined when there is none. */
  readBytes(projectPath: string): Promise<Uint8Array | undefined>;
  /** Replaces a binary file (or writes a new one), atomically where the storage can. Only for files the editor makes, such as compressed copies. */
  replace(projectPath: string, bytes: Uint8Array): Promise<void>;
  /** Size and change time, where the storage keeps them; cheaper than reading the file. */
  stat?(projectPath: string): Promise<{ size: number; mtime: number } | undefined>;
  exists(projectPath: string): Promise<boolean>;
  remove(projectPath: string): Promise<void>;
  /** Moves a file. Refuses to replace one that exists at `to`. */
  move(from: string, to: string): Promise<void>;
  /** Every file under the root whose project path passes `filter`, sorted. */
  list(filter: (projectPath: string) => boolean): Promise<string[]>;
}

/**
 * A fast hash of text, to recognise the project's own writes when they come
 * back from a file watcher. Not for security: 53-bit cyrb53, as hex.
 */
export function hashText(text: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}

/** The same fast hash over bytes, for files with no change time to compare. */
export function hashBytes(bytes: Uint8Array): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < bytes.length; i++) {
    const ch = bytes[i]!;
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}

/** Bytes of base64 text, without Node's Buffer. */
export function fromBase64(text: string): Uint8Array {
  const binary = atob(text);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

/** Base64 text of bytes, without Node's Buffer. */
export function toBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
